import { Injectable } from '@angular/core';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ModalController } from '@ionic/angular';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { PlatformType } from '@panamax/app-state/lib/models/platform.model';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { ShareViaEmailModalComponent } from '@shared/components/share-via-email-modal/share-via-email-modal.component';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { OrderDownloadService } from '@shared/services/file-download/order-download.service';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import {
  ShareEventActions,
  ShareOrderEmailInformation,
} from '@usf/file-share-types';
import {
  SHARE_ORDER_CONFIRMATION_EMAIL_ACTIONS,
  SHARE_REVIEW_ORDER_EMAIL_ACTIONS,
  SHARE_SUBMITTED_ORDER_EMAIL_ACTIONS,
  TRACE_CONTEXT,
} from '@usf/ngrx-file-share';
import { Order } from '@usf/ngrx-order';
import { take } from 'rxjs';
import { productWithAlternativesSelector } from 'src/app/shared/selectors/product.selectors';
import { OrderAnalyticsService } from '../tracking/analytics/order-analytics.service';
import { OrderShareService } from './order-share.service';
import { AlertAnalyticsService } from '@usf/ngrx-alerts';

@Injectable({
  providedIn: 'root',
})
export class OrderShareViaEmailService extends OrderShareService {
  emailTo = '';
  message = '';
  sendCopy = false;
  includeProductPrices = false;
  emailString = '';

  constructor(
    public readonly loadingSpinnerService: LoadingSpinnerService,
    protected panAppState: PanAppState,
    private readonly toastService: ToastService,
    private readonly modalController: ModalController,
    private readonly modalService: ModalService,
    private readonly actionSubject: ActionsSubject,
    private readonly translateService: TranslateService,
    private readonly store: Store,
    private readonly alertAnalyticsService: AlertAnalyticsService,
    private readonly orderAnalyticsService: OrderAnalyticsService,
    private readonly orderDownloadService: OrderDownloadService,
    public readonly platform: PlatformService,
  ) {
    super();
  }

  isSupported(platformType: PlatformType): boolean {
    return platformType === PlatformEnum.desktop;
  }

  shareCart(order: Order) {
    this.showShareViaEmailModal(ShareEventActions.ShareReviewOrder, [order]);
  }

  async shareOrderConfirmation(order: Order[]) {
    this.showShareViaEmailModal(
      ShareEventActions.ShareOrderConfirmation,
      order,
      order[0]?.orderHeader?.tandemOrderNumber,
    );
  }

  async shareSubmittedOrder(order: Order[]) {
    this.showShareViaEmailModal(
      ShareEventActions.ShareSubmittedOrder,
      order,
      order[0]?.orderHeader?.tandemOrderNumber,
    );
  }

  async showShareViaEmailModal(
    shareAction: string,
    order: Order[],
    tandemOrderNumber?: number,
  ) {
    //if 'tandemOrderNumber' exist it means existing order
    const documentName = this.generateDocumentName(
      order[0]?.orderHeader?.customerNumber,
      tandemOrderNumber,
    );

    this.modalService.setModalOptions(
      this.platform?.platformType === this.platform?.platformEnum?.mobile,
      ModalWithNavComponent,
      {
        rootPage: ShareViaEmailModalComponent,
        rootPageParams: {
          documentName: documentName,
          shareAction: shareAction,
          isMobile:
            this.platform?.platformType === this.platform?.platformEnum?.mobile,
        },
      },
      this.platform?.platformType === this.platform?.platformEnum?.mobile
        ? 'share-via-email-modal-mobile'
        : 'share-via-email-modal-desktop',
      true,
    );

    this.orderAnalyticsService.trackShareViaEmailOrderOpenModal(shareAction);

    // alerts analytics
    if (shareAction === ShareEventActions.ShareReviewOrder) {
      this.alertAnalyticsService.trackShareFileReviewOrderModalOpen();
    } else if (
      shareAction === ShareEventActions.ShareOrderConfirmation ||
      shareAction === ShareEventActions.ShareSubmittedOrder
    ) {
      this.alertAnalyticsService.trackShareFileOrderConfirmationModalOpen();
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    await modal.onDidDismiss().then((resp: any) => {
      if (
        !!resp?.data &&
        !!resp?.data?.emailData &&
        !!resp?.data?.emailString &&
        resp?.data?.emailData?.emailTo !== undefined &&
        resp?.data?.emailData?.message !== undefined &&
        resp?.data?.emailData?.sendCopy !== undefined &&
        resp?.data?.emailData?.includeProductPrices !== undefined
      ) {
        this.emailTo = resp?.data?.emailData?.emailTo;
        this.message = resp?.data?.emailData?.message;
        this.sendCopy = resp?.data?.emailData?.sendCopy;
        this.includeProductPrices = resp?.data?.emailData?.includeProductPrices;
        this.emailString = resp?.data?.emailString;

        this.shareViaEmailOrder(shareAction, documentName, order);
      }
    });
  }

  async shareViaEmailOrder(
    shareAction: string,
    documentName: string,
    order: Order[],
  ) {
    try {
      this.loadingSpinnerService.createSpinnerModal().then(async () => {
        const products = await this.store
          .select(productWithAlternativesSelector)
          .pipe(take(1))
          .toPromise();

        const payload = this.orderDownloadService.handleCSVDownload(order, {
          fileName: documentName,
          fileType: DownloadFileTypes.CSV,
          products,
          isCart:
            shareAction !== ShareEventActions.ShareOrderConfirmation &&
            shareAction !== ShareEventActions.ShareSubmittedOrder,
        });

        const user = await this.panAppState.user$.pipe(take(1)).toPromise();

        const shareOrderViaEmailModel: ShareOrderEmailInformation = {
          ccAddresses: this.emailTo?.split(','),
          firstName: user?.firstName,
          lastName: user?.lastName,
          message: this.message,
          sendMeACopy: this.sendCopy,
          sharedBy: user?.email,
          fileName: documentName,
          shareAction: shareAction,
          payload,
        };

        if (shareAction === ShareEventActions.ShareReviewOrder) {
          // alerts analytics
          this.alertAnalyticsService.trackShareFileReviewOrder();

          this.store.dispatch(
            SHARE_REVIEW_ORDER_EMAIL_ACTIONS.shareReviewOrderViaEmail({
              data: shareOrderViaEmailModel,
              tracking: {
                tracing: {
                  data: {
                    traceContext: TRACE_CONTEXT.shareReviewOrderViaEmail,
                    isStartOfTrace: true,
                    isEndOfTrace: false,
                  },
                },
              },
            }),
          );

          this.actionSubject
            .pipe(
              ofType(
                SHARE_REVIEW_ORDER_EMAIL_ACTIONS.shareReviewOrderViaEmailSuccess,
              ),
              take(1),
            )
            .subscribe(() => {
              this.loadingSpinnerService.dismissSpinnerModal();
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.shareReviewOrder.successMessage',
                ) + this.emailString,
                'green-toast',
                MessageTypeEnum.success,
                [],
              );
              this.orderAnalyticsService.trackShareViaEmailOrder(shareAction);
            });

          this.actionSubject
            .pipe(
              ofType(
                SHARE_REVIEW_ORDER_EMAIL_ACTIONS.shareReviewOrderViaEmailFail,
              ),
              take(1),
            )
            .subscribe(() => {
              this.loadingSpinnerService.dismissSpinnerModal();
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.shareReviewOrder.failMessage',
                ) + this.emailString,
                'red-toast',
                MessageTypeEnum.error,
                [],
              );
            });
        } else if (shareAction === ShareEventActions.ShareOrderConfirmation) {
          // alerts analytics
          this.alertAnalyticsService.trackShareFileOrderConfirmation();

          this.store.dispatch(
            SHARE_ORDER_CONFIRMATION_EMAIL_ACTIONS.shareOrderConfirmationViaEmail(
              {
                data: shareOrderViaEmailModel,
                tracking: {
                  tracing: {
                    data: {
                      traceContext:
                        TRACE_CONTEXT.shareOrderConfirmationViaEmail,
                      isStartOfTrace: true,
                      isEndOfTrace: false,
                    },
                  },
                },
              },
            ),
          );

          this.actionSubject
            .pipe(
              ofType(
                SHARE_ORDER_CONFIRMATION_EMAIL_ACTIONS.shareOrderConfirmationViaEmailSuccess,
              ),
              take(1),
            )
            .subscribe(() => {
              this.loadingSpinnerService.dismissSpinnerModal();
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.shareOrderConfirmation.successMessage',
                ) + this.emailString,
                'green-toast',
                MessageTypeEnum.success,
                [],
              );
              this.orderAnalyticsService.trackShareViaEmailOrder(shareAction);
            });

          this.actionSubject
            .pipe(
              ofType(
                SHARE_ORDER_CONFIRMATION_EMAIL_ACTIONS.shareOrderConfirmationViaEmailFail,
              ),
              take(1),
            )
            .subscribe(() => {
              this.loadingSpinnerService.dismissSpinnerModal();
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.shareOrderConfirmation.failMessage',
                ) + this.emailString,
                'red-toast',
                MessageTypeEnum.error,
                [],
              );
            });
        } else if (shareAction === ShareEventActions.ShareSubmittedOrder) {
          // alerts analytics
          this.alertAnalyticsService.trackShareFileOrderConfirmation();

          this.store.dispatch(
            SHARE_SUBMITTED_ORDER_EMAIL_ACTIONS.shareSubmittedOrderViaEmail({
              data: shareOrderViaEmailModel,
              tracking: {
                tracing: {
                  data: {
                    traceContext: TRACE_CONTEXT.shareSubmittedOrderViaEmail,
                    isStartOfTrace: true,
                    isEndOfTrace: false,
                  },
                },
              },
            }),
          );

          this.actionSubject
            .pipe(
              ofType(
                SHARE_SUBMITTED_ORDER_EMAIL_ACTIONS.shareSubmittedOrderViaEmailSuccess,
              ),
              take(1),
            )
            .subscribe(() => {
              this.loadingSpinnerService.dismissSpinnerModal();
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.shareSubmittedOrder.successMessage',
                ) + this.emailString,
                'green-toast',
                MessageTypeEnum.success,
                [],
              );
              this.orderAnalyticsService.trackShareViaEmailOrder(shareAction);
            });

          this.actionSubject
            .pipe(
              ofType(
                SHARE_SUBMITTED_ORDER_EMAIL_ACTIONS.shareSubmittedOrderViaEmailFail,
              ),
              take(1),
            )
            .subscribe(() => {
              this.loadingSpinnerService.dismissSpinnerModal();
              this.toastService.presentToastMsg(
                this.translateService.instant(
                  'i18n.shareSubmittedOrder.failMessage',
                ) + this.emailString,
                'red-toast',
                MessageTypeEnum.error,
                [],
              );
            });
        }
      });
    } catch (error) {
      this.loadingSpinnerService.dismissSpinnerModal();
      console.error('Error sharing review order via email:', error);
    }
  }

  private generateDocumentName(
    customerNumber: number,
    tandemOrderNumber?: number,
  ): string {
    const currentDate = this.formatDate(new Date());

    return !!tandemOrderNumber
      ? `Submitted_Order${tandemOrderNumber}_Cust${customerNumber}_${currentDate}.csv`
      : `ReviewOrderDetail_${customerNumber}_${currentDate}.csv`;
  }
}
